import { getInitData } from "./telegram.js";

const BASE = import.meta.env.VITE_API_URL ?? "";

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only declare a JSON body when we actually have one — otherwise Fastify
  // rejects empty-body requests with "Body cannot be empty when content-type
  // is set to 'application/json'".
  const hasBody = init.body !== undefined && init.body !== null;
  const headers: Record<string, string> = {
    Authorization: `tma ${getInitData()}`,
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (hasBody) headers["Content-Type"] = "application/json";

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${body}`);
  }
  // Use text() + JSON.parse so we can attach the path to JSON errors —
  // iOS Safari's "The string did not match the expected pattern" is otherwise
  // useless for debugging.
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    throw new Error(`${path}: JSON parse failed (${(e as Error).message}); body[0..120]=${text.slice(0, 120)}`);
  }
}

export const api = {
  me: () =>
    call<
      | {
          telegramId: string;
          role: "admin";
          name: string | null;
          isOwner: boolean;
          isDeveloper: boolean;
        }
      | {
          telegramId: string;
          role: "master";
          isOwner: false;
          master: { id: string; name: string; telegramUsername: string | null };
        }
    >("/me"),
  doneReminders: () =>
    call<
      {
        id: string;
        kind: string;
        outcome: string | null;
        status: string;
        actedAt: string;
        createdAt: string;
        client: {
          id: string;
          firstName: string;
          lastName: string | null;
          instagram: string | null;
        };
        closedBy: {
          id: string;
          name: string | null;
          telegramId: string;
        } | null;
      }[]
    >("/reminders/done"),
  scheduledVisits: () =>
    call<
      {
        id: string;
        startedAt: string;
        endedAt: string | null;
        service: string;
        client: {
          id: string;
          firstName: string;
          lastName: string | null;
          phone: string | null;
          instagram: string | null;
        } | null;
        master: { id: string; name: string } | null;
      }[]
    >("/visits/scheduled"),
  addClientNote: (id: string, text: string) =>
    call<{ id: string; text: string; createdAt: string }>(
      `/clients/${id}/notes`,
      { method: "POST", body: JSON.stringify({ text }) },
    ),
  setClientDoNotRemind: (id: string, value: boolean) =>
    call<{ doNotRemind: boolean }>(`/clients/${id}/do-not-remind`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  /** Позначити клієнтку як блогерку (без нагадувань, не «нова»). Адмін+owner. */
  setClientBlogger: (id: string, value: boolean) =>
    call<{ isBlogger: boolean }>(`/clients/${id}/blogger`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  setClientReferralSource: (id: string, source: string) =>
    call<{ referralSource: string | null }>(
      `/clients/${id}/referral-source`,
      { method: "POST", body: JSON.stringify({ source }) },
    ),
  setClientGoogleReview: (id: string, value: boolean) =>
    call<{ googleReviewLeft: boolean }>(`/clients/${id}/google-review`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  setClientTelegramUsername: (id: string, telegramUsername: string | null) =>
    call<{ telegramUsername: string | null }>(
      `/clients/${id}/telegram-username`,
      {
        method: "POST",
        body: JSON.stringify({ telegramUsername }),
      },
    ),
  updateClientContacts: (
    id: string,
    body: {
      phone?: string;
      instagram?: string;
      telegramUsername?: string;
      viberPhone?: string;
    },
  ) =>
    call<{
      phone: string | null;
      instagram: string | null;
      telegramUsername: string | null;
      viberPhone: string | null;
    }>(`/clients/${id}/contacts`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  reviews: (from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    return call<{
      counts: { positive: number; negative: number; mixed: number };
      googleFunnel: { asked: number; left: number; rate: number };
      items: {
        id: string;
        actedAt: string;
        sentiment: "positive" | "negative" | "mixed";
        note: string | null;
        client: {
          id: string;
          firstName: string;
          lastName: string | null;
          instagram: string | null;
          googleReviewLeft: boolean;
        };
        masterName: string | null;
      }[];
    }>(`/reviews${p.toString() ? "?" + p : ""}`);
  },
  clientDetail: (id: string) =>
    call<{
      id: string;
      firstName: string;
      lastName: string | null;
      phone: string | null;
      instagram: string | null;
      notes: string | null;
      balance: number;
      discountPercent: number | null;
      group: string | null;
      birthday: string | null;
      blacklisted: boolean;
      doNotRemind: boolean;
      referralSource: string | null;
      segment: string | null;
      appNotes: { id: string; text: string; createdAt: string }[];
      visits: {
        id: string;
        startedAt: string;
        endedAt: string | null;
        service: string;
        serviceLabel: string | null;
        status: string;
        price: number | null;
        tip: number | null;
        master: { id: string; name: string } | null;
      }[];
    }>(`/clients/${id}`),
  clients: (opts: {
    q?: string;
    page?: number;
    pageSize?: number;
    segment?: string;
    referralSource?: string;
  } = {}) => {
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (opts.page) params.set("page", String(opts.page));
    if (opts.pageSize) params.set("pageSize", String(opts.pageSize));
    if (opts.segment) params.set("segment", opts.segment);
    if (opts.referralSource) params.set("referralSource", opts.referralSource);
    const qs = params.toString();
    return call<{
      total: number;
      items: unknown[];
      page: number;
      pageSize: number;
      segments: Record<string, number>;
      referralSources: Record<string, number>;
    }>(`/clients${qs ? "?" + qs : ""}`);
  },
  reminders: () => call<unknown[]>("/reminders"),
  reminderStats: (from: string, to: string) => {
    const p = new URLSearchParams({ from, to });
    return call<{
      from: string;
      to: string;
      bucket: "day" | "week" | "month";
      total: number;
      reopened: number;
      byOutcome: { outcome: string; count: number }[];
      byKind: { kind: string; count: number }[];
      byAdmin: {
        adminId: string | null;
        name: string | null;
        telegramId: string | null;
        count: number;
        booked: number;
        conversionRate: number;
      }[];
      timeline: { bucket: string; count: number }[];
    }>(`/reminders/stats?${p}`);
  },
  snoozeReminder: (id: string, days: number) =>
    call<unknown>(`/reminders/${id}/snooze`, {
      method: "POST",
      body: JSON.stringify({ days }),
    }),
  skipReminder: (id: string) =>
    call<unknown>(`/reminders/${id}/skip`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  reopenReminder: (id: string, note?: string) =>
    call<unknown>(`/reminders/${id}/reopen`, {
      method: "POST",
      body: JSON.stringify(note?.trim() ? { note: note.trim() } : {}),
    }),
  createManualReminder: (body: {
    clientId: string;
    title: string;
    script?: string;
    priority: "super_urgent" | "urgent" | "normal";
    assigneeAdminId?: string;
  }) =>
    call<{ id: string }>(`/reminders/manual`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteReminder: (id: string) =>
    call<{ ok: true }>(`/reminders/${id}`, { method: "DELETE" }),

  /** Owner-only: list admin accounts (for the "Кому призначити?" selector). */
  listAdmins: () =>
    call<
      {
        id: string;
        telegramId: string;
        name: string | null;
        isPrimary: boolean;
      }[]
    >(`/admins`),

  // ----- General tasks -----
  tasks: () =>
    call<
      {
        id: string;
        title: string;
        body: string | null;
        priority: "super_urgent" | "urgent" | "normal";
        status: string;
        dueAt: string | null;
        createdAt: string;
      }[]
    >("/tasks"),
  createTask: (body: {
    title: string;
    body?: string;
    priority: "super_urgent" | "urgent" | "normal";
    dueAt?: string;
  }) =>
    call<{ id: string }>(`/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  markTaskDone: (id: string) =>
    call<{ id: string }>(`/tasks/${id}/done`, { method: "POST" }),
  deleteTask: (id: string) =>
    call<{ ok: true }>(`/tasks/${id}`, { method: "DELETE" }),

  // ----- Shopping list (masters → Lyuba) -----
  shoppingList: (status: "pending" | "all" | "done,cancelled" = "pending") => {
    const p = new URLSearchParams({ status });
    return call<
      {
        id: string;
        text: string;
        status: "pending" | "done" | "cancelled";
        createdAt: string;
        actedAt: string | null;
        master: { id: string; name: string; telegramId: string | null };
      }[]
    >(`/shopping?${p}`);
  },
  shoppingDone: (id: string) =>
    call<{ ok: true }>(`/shopping/${id}/done`, { method: "POST" }),
  shoppingCancel: (id: string) =>
    call<{ ok: true }>(`/shopping/${id}/cancel`, { method: "POST" }),
  shoppingReopen: (id: string) =>
    call<{ ok: true }>(`/shopping/${id}/reopen`, { method: "POST" }),

  // ----- Client discounts -----
  listClientDiscounts: (clientId: string) =>
    call<
      {
        id: string;
        text: string;
        status: "active" | "applied" | "cancelled";
        createdAt: string;
        appliedAt: string | null;
        cancelledAt: string | null;
      }[]
    >(`/clients/${clientId}/discounts`),
  createDiscount: (clientId: string, text: string) =>
    call<{ id: string }>(`/clients/${clientId}/discounts`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  markDiscountApplied: (id: string) =>
    call<{ id: string }>(`/discounts/${id}/applied`, { method: "POST" }),
  cancelDiscount: (id: string) =>
    call<{ id: string }>(`/discounts/${id}/cancel`, { method: "POST" }),

  // ----- Admin salary (owner+dev only) -----
  adminSalary: (opts: { adminId?: string; month?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.adminId) p.set("adminId", opts.adminId);
    if (opts.month) p.set("month", opts.month);
    const qs = p.toString();
    return call<{
      admins: { id: string; telegramId: string; name: string | null }[];
      month: string;
      monthStart: string;
      monthEnd: string;
      admin: { id: string; telegramId: string; name: string | null };
      totalClosed: number;
      earnedFromTasks: number;
      byKind: { kind: string; count: number; earned: number }[];
      byTimeliness: {
        label: string;
        count: number;
        earned: number;
        weight: number;
      }[];
      byOutcome: { tag: string; count: number; earned: number }[];
      daily: { day: string; count: number; earned: number }[];
      bookedCount: number;
      conversionRate: number;
      conversionBonus: number;
      tonusWarns: number;
      tonusPenalty: number;
      ownerPings: number;
      ownerPenalty: number;
      baseSalary: number;
      reopenedCount: number;
      supersededCount: number;
      finalPay: number;
    }>(`/reports/admin-salary${qs ? "?" + qs : ""}`);
  },

  /** Self-scoped: зп того, хто питає (адмін бачить лише свою). Цикл 23→23. */
  mySalary: (opts: { month?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.month) p.set("month", opts.month);
    const qs = p.toString();
    return call<{
      month: string;
      monthStart: string;
      monthEnd: string;
      totalClosed: number;
      earnedFromTasks: number;
      conversionRate: number;
      conversionBonus: number;
      tonusWarns: number;
      tonusPenalty: number;
      ownerPings: number;
      ownerPenalty: number;
      penaltyDetails: {
        kind: string;
        idleHours: number | null;
        at: string;
        amount: number;
      }[];
      baseSalary: number;
      finalPay: number;
    }>(`/reports/my-salary${qs ? "?" + qs : ""}`);
  },

  /** Roll-up across every admin for one month — owner-only. */
  adminSalarySummary: (opts: { month?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.month) p.set("month", opts.month);
    const qs = p.toString();
    return call<{
      month: string | null;
      rows: {
        adminId: string;
        telegramId: string;
        name: string | null;
        totalClosed: number;
        bookedCount: number;
        conversionRate: number;
        earnedFromTasks: number;
        conversionBonus: number;
        baseSalary: number;
        finalPay: number;
        month: string;
      }[];
    }>(`/reports/admin-salary-summary${qs ? "?" + qs : ""}`);
  },
  snoozedReminders: () =>
    call<
      {
        id: string;
        kind: string;
        dueAt: string;
        createdAt: string;
        client: {
          id: string;
          firstName: string;
          lastName: string | null;
          instagram: string | null;
        };
      }[]
    >("/reminders/snoozed"),
  setReminderContacted: (id: string, contacted: boolean) =>
    call<unknown>(`/reminders/${id}/contacted`, {
      method: "POST",
      body: JSON.stringify({ contacted }),
    }),
  setReminderReferralSource: (id: string, source: string) =>
    call<unknown>(`/reminders/${id}/referral-source`, {
      method: "POST",
      body: JSON.stringify({ source }),
    }),
  setReminderOutcome: (
    id: string,
    outcome:
      | "not_replied"
      | "booked"
      | "declined"
      | "review_positive"
      | "review_negative"
      | "review_mixed",
    note?: string,
  ) =>
    call<unknown>(`/reminders/${id}/outcome`, {
      method: "POST",
      body: JSON.stringify({ outcome, note }),
    }),
  setReminderOutcomes: (id: string, outcomes: string[], note?: string) =>
    call<unknown>(`/reminders/${id}/outcome`, {
      method: "POST",
      body: JSON.stringify({ outcomes, note }),
    }),
  listCash: (opts: { unassigned?: boolean; masterId?: string; from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.unassigned) p.set("unassigned", "true");
    if (opts.masterId) p.set("masterId", opts.masterId);
    if (opts.from) p.set("from", opts.from);
    if (opts.to) p.set("to", opts.to);
    return call<
      {
        id: string;
        occurredOn: string;
        amount: number;
        tip: number;
        service: string | null;
        isWarranty: boolean;
        discountPercent: number;
        rawMessage: string | null;
        payoutId: string | null;
        master: { id: string; name: string } | null;
      }[]
    >(`/cash${p.toString() ? "?" + p : ""}`);
  },
  deleteCash: (id: string) =>
    call<{ ok: true }>(`/cash/${id}`, { method: "DELETE" }),
  /** Owner-only: toggle isRedo (переробка) на існуючому CashEntry. */
  setCashRedo: (id: string, value: boolean) =>
    call<{ id: string; isRedo: boolean }>(`/cash/${id}/redo`, {
      method: "POST",
      body: JSON.stringify({ value }),
    }),
  /** Видалити цілий звіт (всі CashEntry з однаковим createdAt). Owner-only. */
  deleteCashReport: (createdAtIso: string) =>
    call<{ ok: true; deleted: number }>(
      `/cash-reports/${encodeURIComponent(createdAtIso)}`,
      { method: "DELETE" },
    ),
  /** Відмінити оплачене за цей звіт-день: рядки повертаються в «до виплати»,
   *  виплата коригується або видаляється. Owner/dev-only. */
  unpayCashReport: (createdAtIso: string) =>
    call<{
      ok: true;
      unpaidEntries: number;
      deletedPayouts: number;
      adjustedPayouts: number;
    }>(`/cash-reports/${encodeURIComponent(createdAtIso)}/unpay`, {
      method: "POST",
    }),
  allCashReports: () =>
    call<
      {
        id: string;
        master: { id: string; name: string } | null;
        occurredOn: string;
        sentDate: string;
        sentTime: string;
        sentFull: string;
        total: number;
        tips: number;
        entries: {
          id: string;
          service: string | null;
          amount: number;
          tip: number;
          discountPercent: number;
          isWarranty: boolean;
          isBarter: boolean;
          isBlogger?: boolean;
          isRedo?: boolean;
          rawMessage: string | null;
          inPayout: boolean;
        }[];
      }[]
    >("/cash-reports"),
  masterCashReports: (masterId: string) =>
    call<
      {
        id: string;
        occurredOn: string;
        sentDate: string;
        sentTime: string;
        sentFull: string;
        total: number;
        tips: number;
        entries: {
          id: string;
          service: string | null;
          amount: number;
          tip: number;
          discountPercent: number;
          isWarranty: boolean;
          isBarter: boolean;
          isBlogger?: boolean;
          isRedo?: boolean;
          rawMessage: string | null;
          inPayout: boolean;
        }[];
      }[]
    >(`/masters/${masterId}/cash-reports`),
  editCashEntry: (
    id: string,
    fields: {
      service?: string;
      amount?: number;
      tip?: number;
      discountPercent?: number;
    },
  ) =>
    call<{
      id: string;
      service: string | null;
      amount: number;
      tip: number;
      discountPercent: number;
    }>(`/cash/${id}/edit`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),
  reassignCash: (id: string, masterId: string | null) =>
    call<unknown>(`/cash/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ masterId }),
    }),
  payoutMaster: (masterId: string, from: string, to: string, note?: string) =>
    call<{ id: string; amount: number }>(`/masters/${masterId}/payout`, {
      method: "POST",
      body: JSON.stringify({ from, to, note }),
    }),
  /** Owner-only: видати аванс майстру. Створює Advance + Expense одразу. */
  giveAdvance: (masterId: string, amount: number, note?: string) =>
    call<{ id: string; amount: number }>(`/masters/${masterId}/advance`, {
      method: "POST",
      body: JSON.stringify({ amount, note }),
    }),
  listAdvances: (masterId: string) =>
    call<{ id: string; amount: number; occurredOn: string; note: string | null; applied: boolean }[]>(
      `/masters/${masterId}/advances`,
    ),
  /** Портфоліо майстра, сторінками. `total` — скільки фото всього. */
  masterPhotos: (masterId: string, limit = 12, offset = 0) =>
    call<{
      total: number;
      items: {
        id: string;
        url: string;
        width: number | null;
        height: number | null;
        occurredOn: string | null;
        caption: string | null;
        createdAt: string;
      }[];
    }>(
      `/masters/${masterId}/photos?limit=${limit}&offset=${offset}`,
    ),
  masterStats: (masterId: string, from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    return call<any>(`/masters/${masterId}/stats${p.toString() ? "?" + p : ""}`);
  },
  payoutHistory: (masterId: string) =>
    call<
      { id: string; amount: number; paidAt: string; periodFrom: string; periodTo: string }[]
    >(`/masters/${masterId}/payouts`),
  listPayouts: (from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    return call<
      {
        id: string;
        masterId: string;
        masterName: string;
        amount: number;
        fromPrices: number;
        fromTips: number;
        cashEntryCount: number;
        paidAt: string;
        periodFrom: string;
        periodTo: string;
        note: string | null;
      }[]
    >(`/payouts${p.toString() ? "?" + p : ""}`);
  },
  expenseCategories: () =>
    call<
      {
        id: string;
        name: string;
        kind: "fixed" | "variable";
        archived: boolean;
      }[]
    >("/expense-categories"),
  expenses: (from?: string, to?: string) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    return call<
      {
        id: string;
        occurredOn: string;
        amount: number;
        kind: "fixed" | "variable";
        categoryId: string | null;
        categoryName: string;
        note: string | null;
        createdAt: string;
      }[]
    >(`/expenses${p.toString() ? "?" + p : ""}`);
  },
  addExpense: (body: {
    occurredOn: string;
    amount: number;
    categoryId: string;
    note?: string;
  }) =>
    call<{
      id: string;
      occurredOn: string;
      amount: number;
      kind: "fixed" | "variable";
      categoryId: string | null;
      categoryName: string;
      note: string | null;
      createdAt: string;
    }>("/expenses", { method: "POST", body: JSON.stringify(body) }),
  deleteExpense: (id: string) =>
    call<{ ok: true }>(`/expenses/${id}`, { method: "DELETE" }),

  // ----- Resource limits + booking conflicts -----
  resourceLimits: () =>
    call<
      {
        id: string;
        label: string;
        pattern: string;
        capacity: number;
        isActive: boolean;
        createdAt: string;
      }[]
    >("/resource-limits"),
  addResourceLimit: (body: { label: string; pattern: string; capacity: number }) =>
    call<{
      id: string;
      label: string;
      pattern: string;
      capacity: number;
      isActive: boolean;
      createdAt: string;
    }>("/resource-limits", { method: "POST", body: JSON.stringify(body) }),
  patchResourceLimit: (
    id: string,
    body: { label?: string; pattern?: string; capacity?: number; isActive?: boolean },
  ) =>
    call<{
      id: string;
      label: string;
      pattern: string;
      capacity: number;
      isActive: boolean;
    }>(`/resource-limits/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteResourceLimit: (id: string) =>
    call<{ ok: true }>(`/resource-limits/${id}`, { method: "DELETE" }),

  conflicts: (scope: "future" | "past" = "future") =>
    call<
      {
        id: string;
        windowStart: string;
        windowEnd: string;
        detectedAt: string;
        notifiedAt: string | null;
        dismissedAt: string | null;
        resourceLimit: { id: string; label: string; capacity: number };
        visits: {
          id: string;
          startedAt: string;
          endedAt: string | null;
          serviceLabel: string | null;
          status: string;
          client: { id: string; name: string } | null;
          master: { id: string; name: string } | null;
        }[];
      }[]
    >(`/conflicts?scope=${scope}`),
  dismissConflict: (id: string) =>
    call<{ ok: true }>(`/conflicts/${id}/dismiss`, { method: "POST" }),
  scanConflicts: () =>
    call<{ newClusters: number; pushed: number }>("/conflicts/scan", {
      method: "POST",
    }),

  reports: {
    overview: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<{
        from: string;
        to: string;
        // Bumpix activity
        visits: number;
        uniqueClients: number;
        newClients: number;
        bumpixRevenue: number;
        bumpixAvgCheck: number;
        // Telegram cash
        cashRevenue: number;
        cashTips: number;
        cashEntries: number;
        cashAvgCheck: number;
        studioRevenue: number;
        mastersPayout: number;
      }>(`/reports/overview${p.toString() ? "?" + p : ""}`);
    },
    timeline: (opts: { from?: string; to?: string; bucket?: "week" | "month" } = {}) => {
      const p = new URLSearchParams();
      if (opts.from) p.set("from", opts.from);
      if (opts.to) p.set("to", opts.to);
      if (opts.bucket) p.set("bucket", opts.bucket);
      return call<{
        bucket: "week" | "month";
        items: {
          bucket: string;
          visits: number;
          bumpixRevenue: number;
          cashRevenue: number;
          cashTips: number;
        }[];
      }>(`/reports/timeline?${p}`);
    },
    byMaster: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<
        {
          masterId: string;
          name: string;
          visits: number;
          bumpixRevenue: number;
          cashRevenue: number;
          cashTips: number;
          masterTakes: number;
          studioTakes: number;
          paidOut: number;
          pendingPayout: number;
        }[]
      >(`/reports/by-master${p.toString() ? "?" + p : ""}`);
    },
    referralSources: () =>
      call<{ source: string; count: number }[]>("/reports/referral-sources"),
    retention: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<{ newClients: number; returned: number; rate: number }>(
        `/reports/retention${p.toString() ? "?" + p : ""}`,
      );
    },
    forecast: () =>
      call<{
        daysPassed: number;
        daysInMonth: number;
        mtd: { cash: number; bumpix: number };
        forecast: { cash: number; bumpix: number };
        prevMonth: { cash: number; bumpix: number };
      }>("/reports/forecast"),
    heatmap: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<{ dow: number; hour: number; visits: number }[]>(
        `/reports/heatmap${p.toString() ? "?" + p : ""}`,
      );
    },
    profit: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<{
        services: number;
        tips: number;
        masterTakes: number;
        studioGross: number;
        expenses: number;
        studioNet: number;
      }>(`/reports/profit${p.toString() ? "?" + p : ""}`);
    },
    masterRatings: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<
        { masterId: string; name: string; positive: number; negative: number }[]
      >(`/reports/master-ratings${p.toString() ? "?" + p : ""}`);
    },
    topClients: (from?: string, to?: string, limit?: number) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      if (limit) p.set("limit", String(limit));
      return call<
        {
          clientId: string;
          firstName: string;
          lastName: string | null;
          instagram: string | null;
          visits: number;
          spent: number;
          tips: number;
        }[]
      >(`/reports/top-clients${p.toString() ? "?" + p : ""}`);
    },
    services: (from?: string, to?: string) => {
      const p = new URLSearchParams();
      if (from) p.set("from", from);
      if (to) p.set("to", to);
      return call<{ service: string; visits: number; revenue: number }[]>(
        `/reports/services${p.toString() ? "?" + p : ""}`,
      );
    },
  },
  masters: () => call<unknown[]>("/masters"),
  upsertMaster: (body: {
    id?: string;
    name: string;
    telegramId?: string;
    telegramUsername?: string;
    active?: boolean;
    starred?: boolean;
  }) =>
    call<unknown>("/masters", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Задати / змінити / очистити Telegram ID (і username) майстра. */
  setMasterTelegram: (
    id: string,
    body: { telegramId?: string | null; telegramUsername?: string | null },
  ) =>
    call<{
      id: string;
      telegramId: string | null;
      telegramUsername: string | null;
    }>(`/masters/${id}/telegram`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  setMasterStar: (id: string, starred: boolean) =>
    call<unknown>(`/masters/${id}/star`, {
      method: "POST",
      body: JSON.stringify({ starred }),
    }),
  setMasterActive: (id: string, active: boolean) =>
    call<unknown>(`/masters/${id}/active`, {
      method: "POST",
      body: JSON.stringify({ active }),
    }),
};
